'use client';

import { Button } from '@ahafood/ui/components/button';
import { cn } from '@ahm/common-helpers';
import { ArrowLeft } from 'lucide-react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useQueryState } from 'nuqs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FilterDrawer } from './filter-drawer';
import type { Payload } from './simple-store-list';
import { getSearchAutocompleteFn } from '@/api/use-search-autocomplete';
import { trackEvent } from '@/lib/event-tracking';
import {
  createSearchSession,
  clearSearchSessionId,
} from '@/lib/search-session';
import { useAuthStore } from '@/store';

interface FilterBarProps {
  categories: {
    id: number;
    slug: string;
    title_vi_vn: string;
  }[];
  currentPayload: Payload;
  citySlug: string;
  _payload: Payload;
}

const buildFilteredUrl = (
  citySlug: string,
  filters: {
    category_slug?: string;
    promotion?: boolean;
    freeship?: boolean | string;
    special_request?: string;
    radius?: number;
    open_now?: boolean;
    item_tag?: string;
  }
) => {
  let url = `/${citySlug}/danh-sach-cua-hang`;
  if (filters.category_slug) url += `-${filters.category_slug}`;
  const options: string[] = [];
  if (filters.item_tag === 'SOLO_MEAL') options.push('solomeal');
  if (filters.promotion) options.push('co-khuyen-mai');
  if (filters.freeship === 'freeship' || filters.freeship === true)
    options.push('co-freeship');
  if (filters.freeship === 'freeship-100') options.push('co-freeship-100');
  if (filters.radius === 5000) options.push('gan-toi');
  if (filters.open_now) options.push('dang-mo-cua');
  if (filters.special_request === 'new_store') options.push('moi');
  if (options.length > 0) url += '-' + options.join('-');
  return url;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function FilterBar({
  categories,
  currentPayload,
  citySlug,
}: FilterBarProps) {
  const router = useRouter();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { user } = useAuthStore();
  const [tempFilters, setTempFilters] = useState({
    category_slug: currentPayload.category_slug ?? '',
    promotion: currentPayload.promotion ?? false,
    freeship: currentPayload.freeship ?? false,
    special_request: currentPayload.special_request ?? '',
    nearby: currentPayload.radius === 5000,
    open_now: currentPayload.open_now ?? false,
    item_tag: currentPayload.item_tag ?? '',
  });

  const [searchQuery] = useQueryState<string>('text', {
    parse: (v) => (typeof v === 'string' ? v : ''),
    defaultValue: '',
  });

  const { control, watch, setValue } = useForm<{ text: string }>({
    defaultValues: { text: searchQuery ?? '' },
  });
  const textValue = watch('text');
  // Debounced text for autocomplete
  const debouncedText = useDebounce(textValue, 300);

  const isSelectingRef = useRef(false);
  const didInitFromUrl = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentInputValueRef = useRef<string>('');

  // Autocomplete states
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // only autofocus when navigating from StoreHeader
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const shouldAutoFocus = searchParams.get('focus') === '1';
  useEffect(() => {
    if (shouldAutoFocus) {
      inputRef.current?.focus();
      // remove focus param from URL
      const params = new URLSearchParams(searchParams.toString());
      params.delete('focus');
      const newQuery = params.toString();
      router.replace(`${pathname}${newQuery ? `?${newQuery}` : ''}`);
    }
  }, [shouldAutoFocus, searchParams, pathname, router]);

  // navigateWithText (preserve other filters except category)
  const navigateWithText = useCallback(
    (text: string) => {
      // Handle search session management
      if (text && user?.cunstomerId) {
        // Create or update search session
        createSearchSession(user.cunstomerId, text);
      } else if (!text) {
        // Clear search session when text is empty
        clearSearchSessionId();
      }

      const url = buildFilteredUrl(citySlug, {
        category_slug: text ? undefined : currentPayload.category_slug,
        promotion: currentPayload.promotion,
        freeship: currentPayload.freeship,
        special_request: currentPayload.special_request,
        radius: currentPayload.radius,
        open_now: currentPayload.open_now,
        item_tag: currentPayload.item_tag,
      });

      if (text) {
        router.push(`${url}?text=${encodeURIComponent(text)}`);
      } else {
        router.push(url);
      }
    },
    [citySlug, currentPayload, router, user?.cunstomerId]
  );

  useEffect(() => {
    if (!didInitFromUrl.current && searchQuery) {
      // lần đầu load từ URL -> chỉ set value, không mở dropdown
      setValue('text', searchQuery);
      currentInputValueRef.current = searchQuery;
      didInitFromUrl.current = true;
    }
  }, [searchQuery, setValue]);

  // Initialize current input value ref with textValue
  useEffect(() => {
    currentInputValueRef.current = textValue;
  }, [textValue]);
  // fetch suggestions when debounced text changes
  useEffect(() => {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const fetchSuggestions = async () => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (debouncedText && debouncedText.length > 0) {
        if (isSelectingRef.current) {
          isSelectingRef.current = false;
          return;
        }

        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;

        setIsLoading(true);
        try {
          const response = await getSearchAutocompleteFn({
            text: debouncedText,
          });

          // Only update state if request wasn't aborted
          if (!signal.aborted) {
            setSuggestions(response?.suggestions || []);
            if (isFocused) setShowDropdown(true);
          }
        } catch (error) {
          // Only handle error if request wasn't aborted
          if (!signal.aborted) {
            console.error(error);
            setSuggestions([]);
            if (isFocused) setShowDropdown(true);
          }
        } finally {
          // Only update loading state if request wasn't aborted
          if (!signal.aborted) {
            setIsLoading(false);
          }
        }
      } else {
        setSuggestions([]);
        setIsLoading(false);
        setShowDropdown(false);
      }
    };
    void fetchSuggestions();

    // Cleanup function to abort request on unmount or dependency change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [debouncedText, isFocused]);

  // handle keydown: Enter confirms search and closes dropdown
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setShowDropdown(false);
        setIsFocused(false);
        // Use current input value instead of debounced textValue for immediate response
        const currentValue = currentInputValueRef.current;
        navigateWithText(currentValue);
        inputRef.current?.blur();
      }
      // optional: allow Escape to close dropdown
      if (e.key === 'Escape') {
        setShowDropdown(false);
        setIsFocused(false);
        inputRef.current?.blur();
      }
    },
    [navigateWithText]
  );

  // handle blur: don't close dropdown — only mark focus state
  const handleBlur = useCallback(() => {
    // keep a small delay in case suggestion click triggers first
    setTimeout(() => {
      setIsFocused(false);
      // DO NOT setShowDropdown(false) here
    }, 150);
  }, []);

  // handle input focus: mark and if text present open dropdown (suggestions already set by effect)
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (textValue && textValue.length > 0) {
      setShowDropdown(true);
    }
  }, [textValue]);

  // user typing
  const handleTextChange = useCallback(
    (val: string) => {
      // Store current input value for immediate Enter key response
      currentInputValueRef.current = val;
      setValue('text', val);
      // do not navigate immediately — wait for Enter or selection
    },
    [setValue]
  );

  // selecting a suggestion => set text, navigate and close dropdown
  const handleSelectSuggestion = useCallback(
    (suggestion: string) => {
      isSelectingRef.current = true;
      setValue('text', suggestion);

      // Track search suggestion click
      if (user?.cunstomerId) {
        trackEvent('click_button', {
          action_time: Math.floor(Date.now() / 1000),
          button_name: `${suggestion}`,
          button_location: 'search_section',
        });
      }

      navigateWithText(suggestion);
      setIsFocused(false);
      setShowDropdown(false);
      inputRef.current?.blur();
    },
    [setValue, navigateWithText, user?.cunstomerId]
  );

  const handleClearText = useCallback(() => {
    setValue('text', '');
    navigateWithText('');
    setIsFocused(false);
    setShowDropdown(false);
  }, [setValue, navigateWithText]);

  // filter-related handlers (unchanged)
  const navigateWithFilters = useCallback(
    (filters: typeof tempFilters) => {
      const url = buildFilteredUrl(citySlug, {
        category_slug: filters.category_slug || undefined,
        promotion: filters.promotion,
        freeship: filters.freeship,
        special_request: filters.special_request || undefined,
        radius: filters.nearby ? 5000 : undefined,
        open_now: filters.open_now,
        item_tag: filters.item_tag || undefined,
      });
      if (textValue && !filters.category_slug) {
        router.push(`${url}?text=${encodeURIComponent(textValue)}`);
      } else {
        router.push(url);
      }
    },
    [citySlug, router, textValue]
  );

  const handleApplyFilters = useCallback(() => {
    if (tempFilters.category_slug && textValue) {
      setValue('text', '');
      setShowDropdown(false);
    }
    navigateWithFilters(tempFilters);
    setIsFilterOpen(false);
  }, [tempFilters, textValue, setValue, navigateWithFilters]);

  const handleResetFilters = useCallback(() => {
    setTempFilters({
      category_slug: '',
      promotion: false,
      freeship: false,
      special_request: '',
      nearby: false,
      open_now: false,
      item_tag: '',
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setValue('text', '');
    setTempFilters({
      category_slug: '',
      promotion: false,
      freeship: false,
      special_request: '',
      nearby: false,
      open_now: false,
      item_tag: '',
    });
    router.push(`/${citySlug}/danh-sach-cua-hang`);
    setIsFilterOpen(false);
    setShowDropdown(false);
  }, [setValue, router, citySlug]);

  const handleFilterButtonClick = useCallback(
    (filterType: string) => {
      // Track button click event
      const buttonNameMap: Record<string, string> = {
        nearby: 'near_me',
        solomeal: 'solomeal',
        promotion: 'promotion',
        freeship: 'freeship',
        open_now: 'open_now',
      };

      if (buttonNameMap[filterType]) {
        trackEvent('click_button', {
          action_time: Math.floor(Date.now() / 1000),
          button_name: buttonNameMap[filterType],
          button_location: 'search_section',
        });
      }

      const newFilters = { ...tempFilters };

      switch (filterType) {
        case 'solomeal':
          newFilters.item_tag =
            currentPayload.item_tag === 'SOLO_MEAL' ? '' : 'SOLO_MEAL';
          break;
        case 'nearby':
          newFilters.nearby = currentPayload.radius !== 5000;
          break;
        case 'promotion':
          newFilters.promotion = !currentPayload.promotion;
          break;
        case 'freeship':
          newFilters.freeship =
            currentPayload.freeship === 'freeship' ? false : 'freeship';
          break;
        case 'open_now':
          newFilters.open_now = !currentPayload.open_now;
          break;
        default:
          break;
      }

      setTempFilters(newFilters);
      navigateWithFilters(newFilters);
    },
    [currentPayload, navigateWithFilters, tempFilters]
  );

  const getActiveFilterCount = useCallback(() => {
    let count = 0;
    if (currentPayload.category_slug && !textValue) count++;
    if (currentPayload.promotion) count++;
    if (currentPayload.freeship) count++;
    if (currentPayload.special_request) count++;
    if (currentPayload.radius === 5000) count++;
    if (currentPayload.open_now) count++;
    return count;
  }, [currentPayload, textValue]);

  return (
    <>
      <div className="bg-white fixed top-0 inset-x-0 z-50 shadow-sm border-b border-neutral-10">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 pt-4 pb-3">
          <div className="flex gap-2 md:gap-3 mb-3 md:mb-4 items-center">
            <Button
              variant="ghost"
              className="hover:bg-neutral-10 py-2 px-1 rounded-md transition-colors [&_svg]:size-5"
              onClick={() => {
                router.push(`/${citySlug}`);
                trackEvent('click_button', {
                  action_time: Math.floor(Date.now() / 1000),
                  button_name: 'is_back',
                  button_location: 'search_section',
                });
              }}
            >
              <ArrowLeft />
            </Button>

            {currentPayload.special_request !== 'new_store' ? (
              <div className="relative flex-1 flex items-center max-w-2xl">
                <Controller
                  name="text"
                  control={control}
                  render={({ field }) => (
                    <input
                      ref={inputRef}
                      value={field.value}
                      onChange={(e) => handleTextChange(e.target.value)}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onKeyDown={handleKeyDown}
                      placeholder="Tìm cửa hàng..."
                      className="w-full pl-12 pr-4 py-3 bg-neutral-10 rounded-2xl h-11 md:h-12 text-sm md:text-base text-neutral-90 focus:outline-none focus:ring-2 focus:ring-primary-50 transition-all border-none"
                    />
                  )}
                />

                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg
                    className="size-5 text-neutral-50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>

                {textValue && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-40 hover:text-neutral-60 transition-colors"
                    onClick={handleClearText}
                  >
                    <svg
                      className="size-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ) : (
              <div className="w-full text-center pr-8">
                <span className="font-medium text-lg">{`Cửa hàng mới`}</span>
              </div>
            )}
          </div>

          {showDropdown ? (
            /* Autocomplete Suggestions */
            <div className="pb-4 h-dvh">
              <div className="bg-white rounded-lg">
                {isLoading ? (
                  <div className="space-y-1">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div
                        key={index}
                        className="p-3 border-b border-neutral-10 last:border-b-0"
                      >
                        <div className="flex items-center animate-pulse">
                          <div className="size-5 bg-neutral-20 rounded mr-3 shrink-0">
                            <svg
                              className="size-5 text-neutral-30"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          <div
                            className="h-4 bg-neutral-20 rounded flex-1"
                            style={{ width: `${60 + Math.random() * 30}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : suggestions.length > 0 ? (
                  <div className="space-y-0">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        className="w-full px-2 py-4 text-sm text-left hover:bg-neutral-10 cursor-pointer border-b border-neutral-10 last:border-b-0 transition-colors focus:outline-none focus:bg-neutral-10 flex items-center"
                        onClick={() => handleSelectSuggestion(suggestion)}
                        // prevent default on mouseDown to ensure suggestion click registers before blur
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <svg
                          className="size-5 text-neutral-50 mr-3 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        <span className="truncate text-neutral-90">
                          {suggestion}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center text-neutral-50">
                    <svg
                      className="size-12 mx-auto mb-3 text-neutral-30"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <p>Không tìm thấy kết quả phù hợp</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Normal Filter Buttons */
            <div className="flex gap-2 md:gap-3 scrollbar-hide overflow-x-auto items-center">
              <FilterDrawer
                isOpen={isFilterOpen}
                setIsOpen={setIsFilterOpen}
                tempFilters={tempFilters}
                setTempFilters={setTempFilters}
                categories={categories}
                textValue={textValue}
                activeCount={getActiveFilterCount()}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
              />

              {/* quick filters... kept same */}
              <Button
                variant="outline"
                onClick={() => handleFilterButtonClick('nearby')}
                className={cn(
                  'gap-2 whitespace-nowrap rounded-xl border flex items-center px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base text-neutral-70 hover:bg-neutral-5 hover:border-primary-30 transition-all',
                  currentPayload.radius === 5000 &&
                    'border-primary-50 bg-primary-5 text-primary-60 font-semibold'
                )}
              >
                Gần tôi
              </Button>

              <Button
                variant="outline"
                onClick={() => handleFilterButtonClick('solomeal')}
                className={cn(
                  'gap-2 whitespace-nowrap rounded-xl border flex items-center px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base text-neutral-70 hover:bg-neutral-5 hover:border-primary-30 transition-all',
                  currentPayload.item_tag === 'SOLO_MEAL' &&
                    'border-primary-50 bg-primary-5 text-primary-60 font-semibold'
                )}
              >
                SOLOMEAL
              </Button>

              <Button
                variant="outline"
                onClick={() => handleFilterButtonClick('promotion')}
                className={cn(
                  'gap-2 whitespace-nowrap rounded-xl border flex items-center px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base text-neutral-70 hover:bg-neutral-5 hover:border-primary-30 transition-all',
                  currentPayload.promotion &&
                    'border-primary-50 bg-primary-5 text-primary-60 font-semibold'
                )}
              >
                Ưu đãi
              </Button>

              <Button
                variant="outline"
                onClick={() => handleFilterButtonClick('freeship')}
                className={cn(
                  'gap-2 whitespace-nowrap border rounded-xl flex items-center px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base text-neutral-70 hover:bg-neutral-5 hover:border-primary-30 transition-all',
                  currentPayload.freeship === 'freeship' &&
                    'border-primary-50 bg-primary-5 text-primary-60 font-semibold'
                )}
              >
                Freeship
              </Button>

              <Button
                variant="outline"
                onClick={() => handleFilterButtonClick('open_now')}
                className={cn(
                  'gap-2 whitespace-nowrap border rounded-xl flex items-center px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base text-neutral-70 hover:bg-neutral-5 hover:border-primary-30 transition-all',
                  currentPayload.open_now &&
                    'border-primary-50 bg-primary-5 text-primary-60 font-semibold'
                )}
              >
                Mở cửa
              </Button>

              {getActiveFilterCount() > 0 && (
                <Button
                  variant="secondary"
                  onClick={handleClearAllFilters}
                  className="gap-2 whitespace-nowrap bg-red-10 rounded-xl text-red-50 hover:bg-red-20 flex items-center px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base font-medium transition-all"
                >
                  Đặt lại
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Add a generic debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}
